// TASK-2 (2026-07-10) — "Your tasks" card for the Introducer + Partner
// portal dashboards. Shows tasks assigned to the logged-in portal user by
// Solara admins, and lets them tick checklist items, add progress notes,
// and mark their own tasks complete via the tightly-scoped portal
// endpoints (GET /portal/tasks, PUT /portal/tasks/{id}).
//
// Styling follows the PortalShell concierge language: white card, gold
// border, serif kicker, navy/gold accents.

import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, ClipboardList, MessageSquare } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const dueMeta = (dueIso) => {
  if (!dueIso) return { label: "No due date", classes: "bg-gray-100 text-gray-500" };
  try {
    const due = new Date(dueIso);
    const now = new Date();
    const days = Math.floor((due - now) / 86400000);
    const label = due.toLocaleDateString("en-AU", {
      timeZone: "Australia/Sydney", day: "numeric", month: "short",
    });
    if (days < 0) return { label: `Due ${label} — overdue`, classes: "bg-red-100 text-red-700" };
    if (days <= 2) return { label: `Due ${label}`, classes: "bg-amber-100 text-amber-700" };
    return { label: `Due ${label}`, classes: "bg-[#021d49]/5 text-[#021d49]" };
  } catch {
    return { label: dueIso, classes: "bg-gray-100 text-gray-500" };
  }
};

export default function PortalTasksCard() {
  const [tasks, setTasks] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [noteDrafts, setNoteDrafts] = useState({});  // { [taskId:itemId]: text }
  const [notesOpen, setNotesOpen] = useState({});    // { [taskId:itemId]: bool }

  const load = async () => {
    try {
      const res = await axios.get(`${API}/portal/tasks`);
      setTasks(res.data.tasks || []);
    } catch {
      // Silent — the card simply stays empty if tasks can't load.
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (taskId, payload, successMsg) => {
    try {
      await axios.put(`${API}/portal/tasks/${taskId}`, payload);
      if (successMsg) toast.success(successMsg);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update task");
    }
  };

  const toggleItem = (task, item, completed) =>
    save(task.id, { checklist_items: [{ id: item.id, completed }] });

  const addNote = (task, item) => {
    const key = `${task.id}:${item.id}`;
    const text = (noteDrafts[key] || "").trim();
    if (!text) return;
    setNoteDrafts((prev) => ({ ...prev, [key]: "" }));
    save(task.id, { checklist_items: [{ id: item.id, notes: [{ text }] }] }, "Note added");
  };

  const openTasks = tasks.filter((t) => t.status !== "completed");
  const doneCount = tasks.length - openTasks.length;

  // Nothing assigned yet — keep the dashboard clean rather than showing
  // an empty shell.
  if (!loaded || tasks.length === 0) return null;

  return (
    <div className="bg-white border border-[#bd9a59]/30 rounded-xl p-5" data-testid="portal-tasks-card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-serif text-[12px] uppercase tracking-wider text-[#021d49] flex items-center gap-2">
          <ClipboardList className="w-4 h-4 text-[#bd9a59]" />
          Your tasks
        </h2>
        {doneCount > 0 && (
          <span className="text-[11px] text-gray-400">{doneCount} completed</span>
        )}
      </div>

      {openTasks.length === 0 ? (
        <p className="text-sm text-gray-500 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-600" /> All tasks done — nothing outstanding.
        </p>
      ) : (
        <div className="space-y-3">
          {openTasks.map((task) => {
            const due = dueMeta(task.due_at);
            const items = task.checklist_items || [];
            return (
              <div key={task.id} className="border border-gray-200 rounded-lg p-3" data-testid={`portal-task-${task.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#021d49]">{task.title}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      <span className={`text-[11px] px-2 py-0.5 rounded-full ${due.classes}`}>{due.label}</span>
                      {task.related_reference && (
                        <span className="text-[11px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{task.related_reference}</span>
                      )}
                      {task.created_by_name && (
                        <span className="text-[11px] text-gray-400">from {task.created_by_name}</span>
                      )}
                    </div>
                    {task.description && (
                      <p className="text-xs text-gray-600 mt-1.5 whitespace-pre-wrap">{task.description}</p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => save(task.id, { status: "completed" }, "Task completed")}
                    className="shrink-0 text-xs px-3 py-1.5 rounded bg-[#021d49] text-white hover:bg-[#052b66]"
                    data-testid={`portal-task-complete-${task.id}`}
                  >
                    Mark complete
                  </button>
                </div>

                {items.length > 0 && (
                  <div className="mt-2.5 space-y-1.5 border-t border-gray-100 pt-2.5">
                    {items.map((item) => {
                      const key = `${task.id}:${item.id}`;
                      const noteCount = (item.notes || []).length;
                      return (
                        <div key={item.id}>
                          <div className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              checked={Boolean(item.completed)}
                              onChange={(e) => toggleItem(task, item, e.target.checked)}
                            />
                            <span className={`flex-1 text-sm ${item.completed ? "line-through text-gray-400" : "text-gray-700"}`}>
                              {item.text}
                            </span>
                            <button
                              type="button"
                              title="Progress notes"
                              onClick={() => setNotesOpen((prev) => ({ ...prev, [key]: !prev[key] }))}
                              className={`flex items-center gap-0.5 text-[10px] ${noteCount ? "text-[#bd9a59]" : "text-gray-400"} hover:text-[#021d49]`}
                            >
                              <MessageSquare className="w-3.5 h-3.5" />
                              {noteCount > 0 && <span>{noteCount}</span>}
                            </button>
                          </div>
                          {notesOpen[key] && (
                            <div className="ml-6 mt-1 space-y-1 border-l-2 border-[#bd9a59]/40 pl-3">
                              {(item.notes || []).map((note, i) => (
                                <div key={`${item.id}-n-${i}`} className="text-xs text-gray-600">
                                  <span className="whitespace-pre-wrap">{note.text}</span>
                                  <span className="text-gray-400">
                                    {" — "}{note.author_name || note.author_email || ""}
                                    {note.created_at
                                      ? ` · ${new Date(note.created_at).toLocaleDateString("en-AU", { timeZone: "Australia/Sydney", day: "numeric", month: "short" })}`
                                      : ""}
                                  </span>
                                </div>
                              ))}
                              <div className="flex items-center gap-2">
                                <input
                                  value={noteDrafts[key] || ""}
                                  onChange={(e) => setNoteDrafts((prev) => ({ ...prev, [key]: e.target.value }))}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") { e.preventDefault(); addNote(task, item); }
                                  }}
                                  placeholder="Add an update…"
                                  className="flex-1 h-7 text-xs border border-gray-200 rounded px-2"
                                />
                                <button
                                  type="button"
                                  onClick={() => addNote(task, item)}
                                  className="px-2 py-1 text-xs bg-[#021d49] text-white rounded hover:bg-[#052b66]"
                                >
                                  Add
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
